import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import Button from 'components/Button';
import * as React from 'react';
import { tenantConfig } from 'utils/tenant';
import { IVehicle } from 'data/schema';
import { userSession } from 'state/session';
import { classNames } from 'utils/ui/css';
import { getComparisonVehicle } from 'state/session/compare';
import { useElementOverflow } from 'utils/ui/useElementOverflow';

const Wltp: React.FC = () => {
  const { _ } = useLingui();
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const wltpRef = React.useRef<HTMLDivElement>(null);
  const session = userSession().useState();
  const comparisonVehicle = getComparisonVehicle();
  const [, stashedVehicle] = session.comparisonMode === 'on' ? comparisonVehicle : [undefined, undefined];
  const vehicles = [userSession().currentVehicle, stashedVehicle].filter(Boolean) as IVehicle[];

  tenantConfig().useState();
  const showWltp = tenantConfig().isAllowed('wltp');

  const isWltpTruncated = useElementOverflow(wltpRef, '[data-testid="wltp-text"]', [
    vehicles.length,
    session.wltpText,
    stashedVehicle?.wltpText,
  ]);

  if (!showWltp) {
    // Return empty div to make the layout stable independent of WLTP being shown or not
    return <div />;
  }

  return (
    <>
      <div className="ex3d-wltp" ref={wltpRef}>
        {/*
          `line-clamp-2` (-webkit-box + -webkit-line-clamp) is unreliable in
          WebKit when its children use `display:flex` — Safari collapses to a
          single visible line at wider viewports. `[max-height:2lh]` clips at
          two line-heights with normal block flow and renders consistently
          across browsers.
        */}
        <div data-testid="wltp-container" className="overflow-hidden [max-height:2lh]">
          {vehicles.map((vehicle, index) => (
            <div key={index} data-testid="wltp-content" className="flex" part="wltp">
              {vehicles.length > 1 && !!vehicle.wltpText && (
                <span
                  className={classNames(
                    "mr-2 font-['TheGroupTEXTBold'] font-semibold block whitespace-nowrap",
                    isWltpTruncated && 'truncate max-w-24 min-w-12'
                  )}
                >
                  {vehicle.carDisplayName}:
                </span>
              )}
              {/*
                `min-w-0 flex-1` lets the flex item shrink below its content
                width so `truncate` can produce the ellipsis (without
                min-w-0, the span auto-grows to content and scrollWidth ==
                clientWidth, so the overflow check never fires).
              */}
              <span data-testid="wltp-text" className="min-w-0 flex-1 truncate whitespace-nowrap">
                {vehicle.wltpText}
              </span>
            </div>
          ))}
        </div>
      </div>

      {isWltpTruncated && (
        <div className="flex h-full justify-center">
          <button
            className="ml-2 mr-4 whitespace-nowrap font-['TheGroupTEXTBold'] text-xs"
            onClick={() => setIsModalVisible(true)}
          >
            <Trans>Show more</Trans>
          </button>
        </div>
      )}

      {isModalVisible && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/50 text-black transition-opacity">
          <div className="max-h-[90vh] w-1/2 overflow-y-auto rounded-lg bg-white p-10 shadow-lg max-lg:w-2/3">
            <h3 className="text-2xl font-semibold capitalize">
              <Trans>WLTP disclaimer</Trans>
            </h3>
            {vehicles.map((vehicle, index) => {
              return (
                <div key={index}>
                  {/* Show vehicle name if we are comparing and this vehicle has WLTP text */}
                  {vehicles.length > 1 && !!vehicle.wltpText && (
                    <h4 className="mb-2 mt-4 text-xl font-semibold">{vehicle.carDisplayName}</h4>
                  )}
                  <p className="my-4">{vehicle.wltpText}</p>
                </div>
              );
            })}
            <div className="flex justify-end">
              <Button text={_(t`Close`)} onClick={() => setIsModalVisible(false)} className="ex3d-button--primary" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Wltp;
